<template>
  <main>
    <PromoBannerImage
      v-if="isPromoBanner"
      to="https://price.ru/blog/kak-vybrat-idealnuyu-diagonal-televizora-kolbinreview/?utm_source=price_header_bnr&utm_from=header_bnr"
    />
    <client-only v-else>
      <YandexRtbBlock
        :style="{ height: `${heightRtbBlock}px` }"
        :block-id="isTablet ? 'R-A-7399841-4' : 'R-A-7399841-5'"
        :type="isTablet ? '' : 'floorAd'"
        :platform="isTablet ? undefined : 'desktop'"
        is-header
        :render-to="
          isTablet ? 'yandex_rtb_R-A-7399841-4' : 'yandex_rtb_R-A-7399841-5'
        "
        @error="errorRtbBlock"
      />
    </client-only>
    <DelayHydration>
      <div>
        <AppHeader />
      </div>
    </DelayHydration>
    <slot />
  </main>
  <AppFooterImport />
  <client-only>
    <LazyUiGlobalModals />
  </client-only>
  <client-only>
    <LazyDebugPanel v-if="isFirstInteractive && debugPanel" />
  </client-only>
  <client-only>
    <LazyConfirmationCookie v-if="!isCookieConfirm && !isPageSpeedBot" />
  </client-only>
</template>
<script setup lang="ts">
import { watchThrottled, useWindowSize } from '@vueuse/core';
import { useMediaQueryStore } from '@/stores/mediaQuery';
import { useInteractiveStore } from '~/stores/interactive';
import { useLocalStoreListener } from '~/composition/useLocalStore';
import { dayMaxAge, cookieNames, maxAge, cookiesMaxAge } from '~/common/const';
import { useGlobalStore } from '~/stores/global';
import { useFavoriteStore } from '~/stores/favorite';
import { logger } from '~/api/loggerLogic';

const AppFooterImport = defineAsyncComponent(
  () => import('~/components/footer/AppFooter.vue')
);
useServerSeoMeta({
  'google-site-verification': 'ibu3Q8PJMSDgp_AwYv-SEYYHvDdM6scTQjNQbJ4Yn9E',
  'yandex-verification': '73a9660d8e319e0f',
});
const nuxtApp = useNuxtApp();
const { width } = useWindowSize();
const mediaStore = useMediaQueryStore();
const globalStore = useGlobalStore();
const interactiveStore = useInteractiveStore();
const favoriteStore = useFavoriteStore();
const isPromoBanner = ref(false);
const heightRtbBlock = ref(50);
const isFirstInteractive = computed(() => interactiveStore.isFirstInteractive);
const isPageSpeedBot = computed(() => interactiveStore.isPageSpeedBot);
const isWebview = computed(() => globalStore.webview);
useLocalStoreListener();
const debugPanel = computed(() => globalStore.debugPanel);
const isCookieConfirm = computed(() => globalStore.isCookieConfirm);
const isMobile = computed(() => mediaStore.isMobile);
const isTablet = computed(() => mediaStore.isMobile || mediaStore.isTablet);
nuxtApp.hook('page:finish', () => {
  const isShowConsole =
    typeof import.meta.env.VITE_VIEW_LOG_TIME !== 'undefined' &&
    import.meta.env.VITE_VIEW_LOG_TIME === 'true';
  if (globalStore.time > 0 && isShowConsole) {
    nextTick(() => {
      console.log('Page transitionTime: ', +new Date() - globalStore.time);
      const dataPage = {
        from: globalStore.typePageFrom,
        to: globalStore.seoData?.page?.type,
        transitionTime: +new Date() - globalStore.time,
        isMobile: isMobile.value,
        isBot: globalStore.isBot,
        isFirst: !globalStore.typePageFrom,
        requestSumTime: globalStore.requestSumTime,
      };
      globalStore.setIsLogged(false);
      logger.error(JSON.stringify(dataPage));
    });
  }
});

const getBetween = (
  w: number,
  min: number,
  max: number | undefined = 1000000
) => {
  return w >= min && w <= max;
};
const getSize = (w: number) => {
  mediaStore.setIsLessEs(getBetween(w, 0, 319));
  mediaStore.setIsEs(getBetween(w, 320, 623));
  mediaStore.setIsSm(getBetween(w, 624, 767));
  mediaStore.setIsMd(getBetween(w, 768, 1023));
  mediaStore.setIsLg(getBetween(w, 1024, 1279));
  mediaStore.setIsXl(getBetween(w, 1280, 1600));
  mediaStore.setIsXXl(getBetween(w, 1601));
  mediaStore.setIsXMax(getBetween(w, 1601));
};

watchThrottled(
  width,
  (w: number) => {
    mediaStore.setIsMobile(w < 768);
    mediaStore.setIsTablet(w < 1280);
    mediaStore.setIsDesktop(w >= 1280);
    getSize(w);
  },
  { throttle: 300 }
);
const errorRtbBlock = () => {
  if (isTablet.value) {
    isPromoBanner.value = true;
  }
};

onMounted(() => {
  if (!import.meta.env.SSR && isWebview) {
    const domain = window.location.hostname.split('.').slice(-2).join('.');
    const cookieSem = useCookie('sem', {
      domain,
      maxAge: dayMaxAge,
    });
    const cookieDebugPanel = useCookie(cookieNames.debugPanel, {
      maxAge,
    });

    if (globalStore.sem && !cookieSem.value) {
      cookieSem.value = '1';
    }
    if (globalStore.debugPanel) {
      cookieDebugPanel.value = '1';
    }
    const cookie_website_id = useCookie('website_id', {
      path: '/',
      domain,
      maxAge: cookiesMaxAge,
    });
    if (cookie_website_id.value) {
      cookie_website_id.value = undefined;
    }
    interactiveStore.addFunction(globalStore.initCatalog);
  }
  if (!import.meta.env.SSR && window) {
    mediaStore.setIsMobile(window.innerWidth < 768);
    mediaStore.setIsTablet(window.innerWidth < 1280);
    getSize(window.innerWidth);
    window.setFavorite = favoriteStore.externalToggleFavorite;
  }
  if (isTablet.value) {
    globalStore.setYandexBanner(true);
    setTimeout(() => {
      const yaBlock = document.getElementById('yandex_rtb_R-A-7399841-4');
      if (!yaBlock || !yaBlock.innerHTML) globalStore.setYandexBanner(false);
    }, 7000);
  } else {
    globalStore.setYandexBanner(true);
  }
});

onBeforeMount(() => {
  if (!import.meta.env.SSR && window) {
    interactiveStore.startChecked();
  }
});
</script>
<style lang="scss" scoped>
main {
  margin: 0;

  &.container {
    padding-top: 0;
  }
}
</style>
